import React from "react";
import { type EventName, createComponent } from "@lit-labs/react";

type Constructor<T> = { new (): T };

export type BlIcon = import("./components/icon/bl-icon").default;
export type BlIconLoad = CustomEvent<string>;
export type BlIconError = CustomEvent<string>;

/**
 * @tag bl-icon
 * @summary Baklava Icon component
 *
 * @attr [name] Name of the icon to show
 * @cssproperty [font-size] Setting size of icon. Default is current font size in DOM place
 * @cssproperty [color=currentColor] Setting color of icon
 */
export const BlIcon = React.lazy(() =>
  customElements.whenDefined("bl-icon").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlIcon",
      tagName: "bl-icon",
      elementClass: customElements.get("bl-icon") as Constructor<
        import("./components/icon/bl-icon").default
      >,
      events: {
        onBlLoad: "bl-load" as EventName<BlIconLoad>,
        onBlError: "bl-error" as EventName<BlIconError>,
      },
    }),
  }))
);

export type BlAccordion = import("./components/accordion-group/accordion/bl-accordion").default;
export type BlAccordionToggle = CustomEvent<boolean>;

export const BlAccordion = React.lazy(() =>
  customElements.whenDefined("bl-accordion").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlAccordion",
      tagName: "bl-accordion",
      elementClass: customElements.get("bl-accordion") as Constructor<
        import("./components/accordion-group/accordion/bl-accordion").default
      >,
      events: { onBlToggle: "bl-toggle" as EventName<BlAccordionToggle> },
    }),
  }))
);

export type BlSpinner = import("./components/spinner/bl-spinner").default;

/**
 * @tag bl-spinner
 * @summary Baklava Spinner component
 *
 */
export const BlSpinner = React.lazy(() =>
  customElements.whenDefined("bl-spinner").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlSpinner",
      tagName: "bl-spinner",
      elementClass: customElements.get("bl-spinner") as Constructor<
        import("./components/spinner/bl-spinner").default
      >,
    }),
  }))
);

export type BlButton = import("./components/button/bl-button").default;
export type BlButtonClick = CustomEvent<string>;

/**
 * @tag bl-button
 * @summary Baklava Button component
 *
 * @cssproperty [--bl-button-display=inline-block] Sets the display property of button
 * @cssproperty [--bl-button-justify=center] Sets the justify-content property of button
 *
 */
export const BlButton = React.lazy(() =>
  customElements.whenDefined("bl-button").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlButton",
      tagName: "bl-button",
      elementClass: customElements.get("bl-button") as Constructor<
        import("./components/button/bl-button").default
      >,
      events: { onBlClick: "bl-click" as EventName<BlButtonClick> },
    }),
  }))
);

export type BlAlert = import("./components/alert/bl-alert").default;
export type BlAlertClose = CustomEvent<boolean>;

/**
 * @tag bl-alert
 * @summary Baklava Alert component
 */
export const BlAlert = React.lazy(() =>
  customElements.whenDefined("bl-alert").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlAlert",
      tagName: "bl-alert",
      elementClass: customElements.get("bl-alert") as Constructor<
        import("./components/alert/bl-alert").default
      >,
      events: { onBlClose: "bl-close" as EventName<BlAlertClose> },
    }),
  }))
);

export type BlBadge = import("./components/badge/bl-badge").default;

/**
 * @tag bl-badge
 * @summary Baklava Badge component
 *
 * @cssproperty [--bl-badge-bg-color=--bl-color-primary-contrast] Sets the background color of badge
 * @cssproperty [--bl-badge-color=--bl-color-primary] Sets the color of badge
 */
export const BlBadge = React.lazy(() =>
  customElements.whenDefined("bl-badge").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlBadge",
      tagName: "bl-badge",
      elementClass: customElements.get("bl-badge") as Constructor<
        import("./components/badge/bl-badge").default
      >,
    }),
  }))
);

export type BlCalendar = import("./components/calendar/bl-calendar").default;
export type BlCalendarCalendarChange = CustomEvent<Date[]>;

/**
 * @tag bl-calendar
 * @summary Baklava Calendar component
 **/
export const BlCalendar = React.lazy(() =>
  customElements.whenDefined("bl-calendar").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlCalendar",
      tagName: "bl-calendar",
      elementClass: customElements.get("bl-calendar") as Constructor<
        import("./components/calendar/bl-calendar").default
      >,
      events: { onBlCalendarChange: "bl-calendar-change" as EventName<BlCalendarCalendarChange> },
    }),
  }))
);

export type BlCheckbox = import("./components/checkbox-group/checkbox/bl-checkbox").default;
export type BlCheckboxCheckboxChange = CustomEvent<boolean>;
export type BlCheckboxFocus = CustomEvent<string>;
export type BlCheckboxBlur = CustomEvent<string>;
export type BlCheckboxCheckboxInvalid = CustomEvent<ValidityState>;

/**
 * @tag bl-checkbox
 * @summary Baklava Checkbox component
 */
export const BlCheckbox = React.lazy(() =>
  customElements.whenDefined("bl-checkbox").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlCheckbox",
      tagName: "bl-checkbox",
      elementClass: customElements.get("bl-checkbox") as Constructor<
        import("./components/checkbox-group/checkbox/bl-checkbox").default
      >,
      events: {
        onBlCheckboxChange: "bl-checkbox-change" as EventName<BlCheckboxCheckboxChange>,
        onBlFocus: "bl-focus" as EventName<BlCheckboxFocus>,
        onBlBlur: "bl-blur" as EventName<BlCheckboxBlur>,
        onBlCheckboxInvalid: "bl-checkbox-invalid" as EventName<BlCheckboxCheckboxInvalid>,
      },
    }),
  }))
);

export type BlCheckboxGroup = import("./components/checkbox-group/bl-checkbox-group").default;
export type BlCheckboxGroupCheckboxGroupChange = CustomEvent<string[]>;
export type BlCheckboxGroupCheckboxGroupInvalid = CustomEvent<ValidityState>;

/**
 * @tag bl-checkbox-group
 * @summary Baklava Button component
 *
 * @cssproperty [--bl-checkbox-direction=row] Can be used for showing checkbox options as columns instead of rows. Options are `row` or `column`
 */
export const BlCheckboxGroup = React.lazy(() =>
  customElements.whenDefined("bl-checkbox-group").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlCheckboxGroup",
      tagName: "bl-checkbox-group",
      elementClass: customElements.get("bl-checkbox-group") as Constructor<
        import("./components/checkbox-group/bl-checkbox-group").default
      >,
      events: {
        onBlCheckboxGroupChange:
          "bl-checkbox-group-change" as EventName<BlCheckboxGroupCheckboxGroupChange>,
        onBlCheckboxGroupInvalid:
          "bl-checkbox-group-invalid" as EventName<BlCheckboxGroupCheckboxGroupInvalid>,
      },
    }),
  }))
);

export type BlInput = import("./components/input/bl-input").default;
export type BlInputChange = CustomEvent<string>;
export type BlInputInput = CustomEvent<string>;
export type BlInputInvalid = CustomEvent<ValidityState>;

/**
 * @tag bl-input
 * @summary Baklava Input component
 *
 * @cssproperty [--bl-input-padding-start] Sets the padding start
 * @cssproperty [--bl-input-padding-end] Sets the padding end
 */
export const BlInput = React.lazy(() =>
  customElements.whenDefined("bl-input").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlInput",
      tagName: "bl-input",
      elementClass: customElements.get("bl-input") as Constructor<
        import("./components/input/bl-input").default
      >,
      events: {
        onBlChange: "bl-change" as EventName<BlInputChange>,
        onBlInput: "bl-input" as EventName<BlInputInput>,
        onBlInvalid: "bl-invalid" as EventName<BlInputInvalid>,
      },
    }),
  }))
);

export type BlPopover = import("./components/popover/bl-popover").default;
export type BlPopoverPopoverShow = CustomEvent<string>;
export type BlPopoverPopoverHide = CustomEvent<string>;

/**
 * @tag bl-popover
 * @summary Baklava Popover component
 *
 * @cssproperty [--bl-popover-arrow-display=none] - Sets the display of popovers arrow. Set as `block` to make arrow visible.
 * @cssproperty [--bl-popover-background-color=--bl-color-neutral-full] - Sets the background color of popover.
 * @cssproperty [--bl-popover-border-color=--bl-color-primary-highlight] - Sets the border color of popover.
 * @cssproperty [--bl-popover-border-size=1px] - Sets the border size of popover. You can set it to `0px` to not have a border (if you use a custom background color). Always use with a length unit.
 * @cssproperty [--bl-popover-padding=--bl-size-m] - Sets the padding of popover.
 * @cssproperty [--bl-popbover-border-radius=--bl-size-3xs] - Sets the border radius of popover.
 * @cssproperty [--bl-popover-max-width=100vw] - Sets the maximum width of the popover (including border and padding).
 * @cssproperty [--bl-popover-position=fixed] - Sets the position of popover. You can set it to `absolute` if parent element is a fixed positioned element like drawer or dialog.
 */
export const BlPopover = React.lazy(() =>
  customElements.whenDefined("bl-popover").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlPopover",
      tagName: "bl-popover",
      elementClass: customElements.get("bl-popover") as Constructor<
        import("./components/popover/bl-popover").default
      >,
      events: {
        onBlPopoverShow: "bl-popover-show" as EventName<BlPopoverPopoverShow>,
        onBlPopoverHide: "bl-popover-hide" as EventName<BlPopoverPopoverHide>,
      },
    }),
  }))
);

export type BlTooltip = import("./components/tooltip/bl-tooltip").default;
export type BlTooltipTooltipShow = CustomEvent<string>;
export type BlTooltipTooltipHide = CustomEvent<string>;

/**
 * @tag bl-tooltip
 * @summary Baklava Tooltip component
 * @dependency bl-popover
 *
 * @cssproperty [--bl-tooltip-trigger-display=inline-flex] Set the display of the tooltip trigger.
 */
export const BlTooltip = React.lazy(() =>
  customElements.whenDefined("bl-tooltip").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTooltip",
      tagName: "bl-tooltip",
      elementClass: customElements.get("bl-tooltip") as Constructor<
        import("./components/tooltip/bl-tooltip").default
      >,
      events: {
        onBlTooltipShow: "bl-tooltip-show" as EventName<BlTooltipTooltipShow>,
        onBlTooltipHide: "bl-tooltip-hide" as EventName<BlTooltipTooltipHide>,
      },
    }),
  }))
);

export type BlDatepicker = import("./components/datepicker/bl-datepicker").default;
export type BlDatepickerDatepickerChange = CustomEvent<Date[]>;

/**
 * @tag bl-datepicker
 * @summary Baklava DatePicker component
 *
 * @cssproperty [--bl-datepicker-input-width] - Sets the width of datepicker input
 **/
export const BlDatepicker = React.lazy(() =>
  customElements.whenDefined("bl-datepicker").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlDatepicker",
      tagName: "bl-datepicker",
      elementClass: customElements.get("bl-datepicker") as Constructor<
        import("./components/datepicker/bl-datepicker").default
      >,
      events: {
        onBlDatepickerChange: "bl-datepicker-change" as EventName<BlDatepickerDatepickerChange>,
      },
    }),
  }))
);

export type BlDialog = import("./components/dialog/bl-dialog").default;
export type BlDialogDialogOpen = CustomEvent<object>;
export type BlDialogDialogRequestClose = CustomEvent<{
  source: "close-button" | "keyboard" | "backdrop";
}>;
export type BlDialogDialogClose = CustomEvent<object>;

/**
 * @tag bl-dialog
 * @summary Baklava Dialog component
 *
 * @cssproperty [--bl-dialog-width=auto] Sets the width of the dialog content
 * @cssproperty [--bl-dialog-caption-line-clamp=1] Sets the line clamp of the caption
 */
export const BlDialog = React.lazy(() =>
  customElements.whenDefined("bl-dialog").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlDialog",
      tagName: "bl-dialog",
      elementClass: customElements.get("bl-dialog") as Constructor<
        import("./components/dialog/bl-dialog").default
      >,
      events: {
        onBlDialogOpen: "bl-dialog-open" as EventName<BlDialogDialogOpen>,
        onBlDialogRequestClose: "bl-dialog-request-close" as EventName<BlDialogDialogRequestClose>,
        onBlDialogClose: "bl-dialog-close" as EventName<BlDialogDialogClose>,
      },
    }),
  }))
);

export type BlDrawer = import("./components/drawer/bl-drawer").default;
export type BlDrawerDrawerOpen = CustomEvent<string>;
export type BlDrawerDrawerClose = CustomEvent<string>;

/**
 * @tag bl-drawer
 * @summary Baklava Drawer component
 *
 * @cssproperty [--bl-drawer-animation-duration=250ms] Drawer slide in animation duration
 */
export const BlDrawer = React.lazy(() =>
  customElements.whenDefined("bl-drawer").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlDrawer",
      tagName: "bl-drawer",
      elementClass: customElements.get("bl-drawer") as Constructor<
        import("./components/drawer/bl-drawer").default
      >,
      events: {
        onBlDrawerOpen: "bl-drawer-open" as EventName<BlDrawerDrawerOpen>,
        onBlDrawerClose: "bl-drawer-close" as EventName<BlDrawerDrawerClose>,
      },
    }),
  }))
);

export type BlSplitButton = import("./components/split-button/bl-split-button").default;
export type BlSplitButtonDropdownOpen = CustomEvent<string>;
export type BlSplitButtonDropdownClose = CustomEvent<string>;
export type BlSplitButtonClick = CustomEvent<string>;

/**
 * @tag bl-split-button
 * @summary Baklava Split Button component
 */
export const BlSplitButton = React.lazy(() =>
  customElements.whenDefined("bl-split-button").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlSplitButton",
      tagName: "bl-split-button",
      elementClass: customElements.get("bl-split-button") as Constructor<
        import("./components/split-button/bl-split-button").default
      >,
      events: {
        onBlDropdownOpen: "bl-dropdown-open" as EventName<BlSplitButtonDropdownOpen>,
        onBlDropdownClose: "bl-dropdown-close" as EventName<BlSplitButtonDropdownClose>,
        onBlClick: "bl-click" as EventName<BlSplitButtonClick>,
      },
    }),
  }))
);

export type BlDropdownGroup = import("./components/dropdown/group/bl-dropdown-group").default;

/**
 * @tag bl-dropdown-group
 * @summary Baklava Dropdown Group component
 */
export const BlDropdownGroup = React.lazy(() =>
  customElements.whenDefined("bl-dropdown-group").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlDropdownGroup",
      tagName: "bl-dropdown-group",
      elementClass: customElements.get("bl-dropdown-group") as Constructor<
        import("./components/dropdown/group/bl-dropdown-group").default
      >,
    }),
  }))
);

export type BlDropdownItem = import("./components/dropdown/item/bl-dropdown-item").default;
export type BlDropdownItemDropdownItemClick = CustomEvent<string>;

/**
 * @tag bl-dropdown-item
 * @summary Baklava Dropdown Item component
 */
export const BlDropdownItem = React.lazy(() =>
  customElements.whenDefined("bl-dropdown-item").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlDropdownItem",
      tagName: "bl-dropdown-item",
      elementClass: customElements.get("bl-dropdown-item") as Constructor<
        import("./components/dropdown/item/bl-dropdown-item").default
      >,
      events: {
        onBlDropdownItemClick:
          "bl-dropdown-item-click" as EventName<BlDropdownItemDropdownItemClick>,
      },
    }),
  }))
);

export type BlDropdown = import("./components/dropdown/bl-dropdown").default;
export type BlDropdownDropdownOpen = CustomEvent<string>;
export type BlDropdownDropdownClose = CustomEvent<string>;

/**
 * @tag bl-dropdown
 * @summary Baklava Dropdown component
 */
export const BlDropdown = React.lazy(() =>
  customElements.whenDefined("bl-dropdown").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlDropdown",
      tagName: "bl-dropdown",
      elementClass: customElements.get("bl-dropdown") as Constructor<
        import("./components/dropdown/bl-dropdown").default
      >,
      events: {
        onBlDropdownOpen: "bl-dropdown-open" as EventName<BlDropdownDropdownOpen>,
        onBlDropdownClose: "bl-dropdown-close" as EventName<BlDropdownDropdownClose>,
      },
    }),
  }))
);

export type BlLink = import("./components/link/bl-link").default;

/**
 * @tag bl-link
 * @summary Baklava Link component for navigation
 *
 * @slot icon - Custom icon slot for non-standalone variants
 *
 * @cssproperty [--bl-link-color=--bl-color-primary] Sets the color of link
 * @cssproperty [--bl-link-hover-color=--bl-color-primary-hover] Sets the hover color of link
 * @cssproperty [--bl-link-active-color=--bl-color-primary-active] Sets the active color of link
 */
export const BlLink = React.lazy(() =>
  customElements.whenDefined("bl-link").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlLink",
      tagName: "bl-link",
      elementClass: customElements.get("bl-link") as Constructor<
        import("./components/link/bl-link").default
      >,
    }),
  }))
);

export type BlNotificationCard =
  import("./components/notification/card/bl-notification-card").default;
export type BlNotificationCardNotificationCardRequestClose = CustomEvent<{
  source: "close-button" | "duration-ended";
}>;
export type BlNotificationCardNotificationCardClose = CustomEvent<{
  source: "close-button" | "duration-ended";
}>;

/**
 * @tag bl-notification-card
 * @summary Baklava Notification Card component
 */
export const BlNotificationCard = React.lazy(() =>
  customElements.whenDefined("bl-notification-card").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlNotificationCard",
      tagName: "bl-notification-card",
      elementClass: customElements.get("bl-notification-card") as Constructor<
        import("./components/notification/card/bl-notification-card").default
      >,
      events: {
        onBlNotificationCardRequestClose:
          "bl-notification-card-request-close" as EventName<BlNotificationCardNotificationCardRequestClose>,
        onBlNotificationCardClose:
          "bl-notification-card-close" as EventName<BlNotificationCardNotificationCardClose>,
      },
    }),
  }))
);

export type BlNotification = import("./components/notification/bl-notification").default;

/**
 * @tag bl-notification
 * @summary Baklava Notification component
 */
export const BlNotification = React.lazy(() =>
  customElements.whenDefined("bl-notification").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlNotification",
      tagName: "bl-notification",
      elementClass: customElements.get("bl-notification") as Constructor<
        import("./components/notification/bl-notification").default
      >,
    }),
  }))
);

export type BlSelectOption = import("./components/select/option/bl-select-option").default;
export type BlSelectOptionSelectOption = CustomEvent<string | null>;
export type BlSelectOptionFocus = CustomEvent<string | null>;
export type BlSelectOptionBlur = CustomEvent<string | null>;

export const BlSelectOption = React.lazy(() =>
  customElements.whenDefined("bl-select-option").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlSelectOption",
      tagName: "bl-select-option",
      elementClass: customElements.get("bl-select-option") as Constructor<
        import("./components/select/option/bl-select-option").default
      >,
      events: {
        onBlSelectOption: "bl-select-option" as EventName<BlSelectOptionSelectOption>,
        onBlFocus: "bl-focus" as EventName<BlSelectOptionFocus>,
        onBlBlur: "bl-blur" as EventName<BlSelectOptionBlur>,
      },
    }),
  }))
);

export type BlSelect = import("./components/select/bl-select").default;
export type BlSelectSelect = CustomEvent<
  | import("./components/select/bl-select").ISelectOption<string>[]
  | import("./components/select/bl-select").ISelectOption<string>
>;
export type BlSelectSearch = CustomEvent<string>;

/**
 * @tag bl-select
 * @summary Baklava Select component
 *
 * @cssproperty [--bl-popover-position=fixed] Sets the positioning strategy of select popover. You can set it as `absolute` if you need to show popover relative to its trigger element.
 */
export const BlSelect = React.lazy(() =>
  customElements.whenDefined("bl-select").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlSelect",
      tagName: "bl-select",
      elementClass: customElements.get("bl-select") as Constructor<
        import("./components/select/bl-select").default
      >,
      events: {
        onBlSelect: "bl-select" as EventName<BlSelectSelect>,
        onBlSearch: "bl-search" as EventName<BlSelectSearch>,
      },
    }),
  }))
);

export type BlPagination = import("./components/pagination/bl-pagination").default;
export type BlPaginationChange = CustomEvent<{
  selectedPage: number;
  prevPage: number;
  itemsPerPage: number;
}>;

/**
 * @tag bl-pagination
 * @summary Baklava Pagination component
 */
export const BlPagination = React.lazy(() =>
  customElements.whenDefined("bl-pagination").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlPagination",
      tagName: "bl-pagination",
      elementClass: customElements.get("bl-pagination") as Constructor<
        import("./components/pagination/bl-pagination").default
      >,
      events: { onBlChange: "bl-change" as EventName<BlPaginationChange> },
    }),
  }))
);

export type BlProgressIndicator =
  import("./components/progress-indicator/bl-progress-indicator").default;

/**
 * @tag bl-progress-indicator
 * @summary Baklava Progress Indicator component
 *
 * @cssproperty [--bl-progress-indicator-transition-duration=.2s] Duration of the transition of progress bar
 * @property {max} [max=100]
 * @property {number} [value=0]
 */
export const BlProgressIndicator = React.lazy(() =>
  customElements.whenDefined("bl-progress-indicator").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlProgressIndicator",
      tagName: "bl-progress-indicator",
      elementClass: customElements.get("bl-progress-indicator") as Constructor<
        import("./components/progress-indicator/bl-progress-indicator").default
      >,
    }),
  }))
);

export type BlRadio = import("./components/radio-group/radio/bl-radio").default;
export type BlRadioChecked = CustomEvent<string>;
export type BlRadioFocus = CustomEvent<string>;
export type BlRadioBlur = CustomEvent<string>;

/**
 * @tag bl-radio
 * @summary Baklava Radio Option component
 *
 * @cssprop [--bl-radio-align-items=center] Align items of radio option
 */
export const BlRadio = React.lazy(() =>
  customElements.whenDefined("bl-radio").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlRadio",
      tagName: "bl-radio",
      elementClass: customElements.get("bl-radio") as Constructor<
        import("./components/radio-group/radio/bl-radio").default
      >,
      events: {
        onBlChecked: "bl-checked" as EventName<BlRadioChecked>,
        onBlFocus: "bl-focus" as EventName<BlRadioFocus>,
        onBlBlur: "bl-blur" as EventName<BlRadioBlur>,
      },
    }),
  }))
);

export type BlRadioGroup = import("./components/radio-group/bl-radio-group").default;
export type BlRadioGroupRadioChange = CustomEvent<string>;

/**
 * @tag bl-radio-group
 * @summary Baklava Button component
 *
 * @cssproperty [--bl-radio-direction=row] Can be used for showing radio options as columns instead of rows. Options are `row` or `column`
 * @cssproperty [--bl-radio-group-cross-axis-item-alignment=normal] Can be used for aligning radio items on cross axis. Acts same with align-item
 * @cssproperty [--bl-radio-group-cross-axis-content-alignment=normal] Can be used for aligning radio group content on cross axis. Acts same with align-content
 * @cssproperty [--bl-radio-group-main-axis-content-alignment=normal] Can be used for aligning radio group content on main axis. Acts same with justify-content
 */
export const BlRadioGroup = React.lazy(() =>
  customElements.whenDefined("bl-radio-group").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlRadioGroup",
      tagName: "bl-radio-group",
      elementClass: customElements.get("bl-radio-group") as Constructor<
        import("./components/radio-group/bl-radio-group").default
      >,
      events: { onBlRadioChange: "bl-radio-change" as EventName<BlRadioGroupRadioChange> },
    }),
  }))
);

export type BlStepperItem = import("./components/stepper/bl-stepper-item").default;
export type BlStepperItemStepperItemClick = CustomEvent<string>;

/**
 * @tag bl-stepper-item
 * @summary Baklava Stepper Item component for individual steps in a stepper
 *
 * @slot default - Step content (title and description)
 */
export const BlStepperItem = React.lazy(() =>
  customElements.whenDefined("bl-stepper-item").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlStepperItem",
      tagName: "bl-stepper-item",
      elementClass: customElements.get("bl-stepper-item") as Constructor<
        import("./components/stepper/bl-stepper-item").default
      >,
      events: {
        onBlStepperItemClick: "bl-stepper-item-click" as EventName<BlStepperItemStepperItemClick>,
      },
    }),
  }))
);

export type BlStepper = import("./components/stepper/bl-stepper").default;
export type BlStepperStepperChange = CustomEvent<{ activeStep: number; totalSteps: number }>;

/**
 * @tag bl-stepper
 * @summary Baklava Stepper component for displaying progress through a sequence of steps
 *
 * @slot default - Stepper items
 */
export const BlStepper = React.lazy(() =>
  customElements.whenDefined("bl-stepper").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlStepper",
      tagName: "bl-stepper",
      elementClass: customElements.get("bl-stepper") as Constructor<
        import("./components/stepper/bl-stepper").default
      >,
      events: { onBlStepperChange: "bl-stepper-change" as EventName<BlStepperStepperChange> },
    }),
  }))
);

export type BlSwitch = import("./components/switch/bl-switch").default;
export type BlSwitchSwitchToggle = CustomEvent<boolean>;

/**
 * @tag bl-switch
 * @summary Baklava Switch component
 *
 * @cssproperty [--bl-switch-color-on=--bl-color-primary] Set the checked color
 * @cssproperty [--bl-switch-color-off=--bl-color-neutral-lighter] Set the unchecked color
 * @cssproperty [--bl-switch-animation-duration=300ms] Set the animation duration of switch toggle
 */
export const BlSwitch = React.lazy(() =>
  customElements.whenDefined("bl-switch").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlSwitch",
      tagName: "bl-switch",
      elementClass: customElements.get("bl-switch") as Constructor<
        import("./components/switch/bl-switch").default
      >,
      events: { onBlSwitchToggle: "bl-switch-toggle" as EventName<BlSwitchSwitchToggle> },
    }),
  }))
);

export type BlTabPanel = import("./components/tab-group/tab-panel/bl-tab-panel").default;

/**
 * @tag bl-tab-panel
 * @summary Baklava Tab panel component
 */
export const BlTabPanel = React.lazy(() =>
  customElements.whenDefined("bl-tab-panel").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTabPanel",
      tagName: "bl-tab-panel",
      elementClass: customElements.get("bl-tab-panel") as Constructor<
        import("./components/tab-group/tab-panel/bl-tab-panel").default
      >,
    }),
  }))
);

export type BlTab = import("./components/tab-group/tab/bl-tab").default;
export type BlTabTabSelected = CustomEvent<string>;

/**
 * @tag bl-tab
 * @summary Baklava Tab component
 */
export const BlTab = React.lazy(() =>
  customElements.whenDefined("bl-tab").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTab",
      tagName: "bl-tab",
      elementClass: customElements.get("bl-tab") as Constructor<
        import("./components/tab-group/tab/bl-tab").default
      >,
      events: { onBlTabSelected: "bl-tab-selected" as EventName<BlTabTabSelected> },
    }),
  }))
);

export type BlTabGroup = import("./components/tab-group/bl-tab-group").default;

/**
 * @tag bl-tab-group
 * @summary Baklava Tab group component
 */
export const BlTabGroup = React.lazy(() =>
  customElements.whenDefined("bl-tab-group").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTabGroup",
      tagName: "bl-tab-group",
      elementClass: customElements.get("bl-tab-group") as Constructor<
        import("./components/tab-group/bl-tab-group").default
      >,
    }),
  }))
);

export type BlTableBody = import("./components/table/table-body/bl-table-body").default;

/**
 * @tag bl-table-body
 * @summary Baklava Table component
 *
 * @slot no-data - Content to display when no data is available
 */
export const BlTableBody = React.lazy(() =>
  customElements.whenDefined("bl-table-body").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTableBody",
      tagName: "bl-table-body",
      elementClass: customElements.get("bl-table-body") as Constructor<
        import("./components/table/table-body/bl-table-body").default
      >,
    }),
  }))
);

export type BlTableHeaderCell =
  import("./components/table/table-header-cell/bl-table-header-cell").default;

/**
 * @tag bl-table-header-cell
 * @summary Baklava Table component
 *
 * @cssproperty [--bl-table-header-cell-width] Set the column width
 * @cssproperty [--bl-table-header-cell-min-width] Set the column min width
 */
export const BlTableHeaderCell = React.lazy(() =>
  customElements.whenDefined("bl-table-header-cell").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTableHeaderCell",
      tagName: "bl-table-header-cell",
      elementClass: customElements.get("bl-table-header-cell") as Constructor<
        import("./components/table/table-header-cell/bl-table-header-cell").default
      >,
    }),
  }))
);

export type BlTableHeader = import("./components/table/table-header/bl-table-header").default;

/**
 * @tag bl-table-header
 * @summary Baklava Table component
 */
export const BlTableHeader = React.lazy(() =>
  customElements.whenDefined("bl-table-header").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTableHeader",
      tagName: "bl-table-header",
      elementClass: customElements.get("bl-table-header") as Constructor<
        import("./components/table/table-header/bl-table-header").default
      >,
    }),
  }))
);

export type BlTableRow = import("./components/table/table-row/bl-table-row").default;

/**
 * @tag bl-table-row
 * @summary Baklava Table component
 */
export const BlTableRow = React.lazy(() =>
  customElements.whenDefined("bl-table-row").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTableRow",
      tagName: "bl-table-row",
      elementClass: customElements.get("bl-table-row") as Constructor<
        import("./components/table/table-row/bl-table-row").default
      >,
    }),
  }))
);

export type BlTableCell = import("./components/table/table-cell/bl-table-cell").default;

/**
 * @tag bl-table-cell
 * @summary Baklava Table component
 */
export const BlTableCell = React.lazy(() =>
  customElements.whenDefined("bl-table-cell").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTableCell",
      tagName: "bl-table-cell",
      elementClass: customElements.get("bl-table-cell") as Constructor<
        import("./components/table/table-cell/bl-table-cell").default
      >,
    }),
  }))
);

export type BlTable = import("./components/table/bl-table").default;
export type BlTableSort = CustomEvent<string[]>;
export type BlTableRowSelect = CustomEvent<string[]>;

/**
 * @tag bl-table
 * @summary Baklava Table component
 *
 */
export const BlTable = React.lazy(() =>
  customElements.whenDefined("bl-table").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTable",
      tagName: "bl-table",
      elementClass: customElements.get("bl-table") as Constructor<
        import("./components/table/bl-table").default
      >,
      events: {
        onBlSort: "bl-sort" as EventName<BlTableSort>,
        onBlRowSelect: "bl-row-select" as EventName<BlTableRowSelect>,
      },
    }),
  }))
);

export type BlTag = import("./components/tag/bl-tag").default;
export type BlTagTagClick = CustomEvent<{ value: string; selected: boolean }>;

/**
 * @tag bl-tag
 * @summary Baklava Tag component
 */
export const BlTag = React.lazy(() =>
  customElements.whenDefined("bl-tag").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTag",
      tagName: "bl-tag",
      elementClass: customElements.get("bl-tag") as Constructor<
        import("./components/tag/bl-tag").default
      >,
      events: { onBlTagClick: "bl-tag-click" as EventName<BlTagTagClick> },
    }),
  }))
);

export type BlTextarea = import("./components/textarea/bl-textarea").default;
export type BlTextareaInput = CustomEvent<string>;
export type BlTextareaChange = CustomEvent<string>;
export type BlTextareaInvalid = CustomEvent<ValidityState>;

/**
 * @tag bl-textarea
 * @summary Baklava Textarea component
 */
export const BlTextarea = React.lazy(() =>
  customElements.whenDefined("bl-textarea").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlTextarea",
      tagName: "bl-textarea",
      elementClass: customElements.get("bl-textarea") as Constructor<
        import("./components/textarea/bl-textarea").default
      >,
      events: {
        onBlInput: "bl-input" as EventName<BlTextareaInput>,
        onBlChange: "bl-change" as EventName<BlTextareaChange>,
        onBlInvalid: "bl-invalid" as EventName<BlTextareaInvalid>,
      },
    }),
  }))
);

export type BlAccordionGroup = import("./components/accordion-group/bl-accordion-group").default;

export const BlAccordionGroup = React.lazy(() =>
  customElements.whenDefined("bl-accordion-group").then(() => ({
    default: createComponent({
      react: React,
      displayName: "BlAccordionGroup",
      tagName: "bl-accordion-group",
      elementClass: customElements.get("bl-accordion-group") as Constructor<
        import("./components/accordion-group/bl-accordion-group").default
      >,
    }),
  }))
);
